import {
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useRef,
  type ReactNode,
  type RefObject,
} from 'react';

/**
 * O MIOLO da folha de datas do palco estreito — a mesma anatomia para voo,
 * hotel, ônibus e carro (#3446, #5662): a linha dos dias da semana pregada no
 * topo, o calendário rolando no meio com um mês por parada, e o rodapé (resumo
 * + ações) pregado embaixo, fora do rolo.
 *
 * A regra que esta casca protege é a do #3197: NADA que a pessoa precise
 * alcançar mora dentro do elemento que rola. O Confirmar no fim da coluna
 * ficava a doze meses de distância. Quem rola é só o `children`.
 *
 * Sem setas de mês (#5662): no celular rolar é a navegação, e a coluna cresce
 * sozinha perto do fim (`useGrowNearEnd`). A linha D S T Q Q S S sai de cada
 * mês e fica uma só, presa sob o cabeçalho (`header`).
 *
 * Vive dentro da casca de folha (`PageFieldSheet`) que já traz o cabeçalho: a
 * raiz assume `flex-1` e mede pela folha.
 */
export function DateSheetBody({
  header,
  footer,
  scrollerRef,
  children,
}: {
  /** Pregado acima do rolo, com a borda inferior (a linha dos dias da semana). */
  readonly header?: ReactNode;
  /** Resumo e ações pregados embaixo. Ausente ⇒ sem rodapé (o multi do voo grava
   *  no toque e não confirma). */
  readonly footer?: ReactNode;
  /** O elemento que rola — para `useGrowNearEnd` medir. */
  readonly scrollerRef?: RefObject<HTMLDivElement | null>;
  /** O que rola. Padding e vãos são do filho: o rolo só recorta e rola. */
  readonly children: ReactNode;
}): ReactNode {
  return (
    <div data-slot="date-sheet-body" className="relative flex min-h-0 flex-1 flex-col">
      {header && <div className="flex-none border-b border-border">{header}</div>}
      {/* `snap-mandatory`: cada mês é uma parada. Sem barra de rolagem: no
          celular ela só ocupa a coluna do sábado, e o dedo não a usa. Sem
          `scroll-smooth` no CSS (#3605): ele animava TODO scroll programático,
          inclusive o do foco no dia tocado, e o snap terminava a animação em
          outro mês. */}
      <div
        ref={scrollerRef}
        data-slot="date-sheet-scroller"
        className="min-h-0 flex-1 snap-y snap-mandatory overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {children}
      </div>
      {footer && (
        <div className="flex flex-none flex-col gap-3 border-t border-border px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))]">
          {footer}
        </div>
      )}
    </div>
  );
}

/** A que distância do fim o rolo pede mais meses: uma tela e meia antes de
 *  acabar, para a pessoa nunca ver o fim da coluna. */
const NEAR_END_SCREENS = 1.5;

/**
 * Pede mais meses quando o rolo da folha se aproxima do fim — a coluna é
 * infinita (#3446). Sem layout (jsdom) não há o que medir e nada cresce.
 */
export function useGrowNearEnd(
  scrollerRef: RefObject<HTMLDivElement | null>,
  {
    enabled,
    onNearEnd,
  }: {
    readonly enabled: boolean;
    /** Chamado (uma vez por aproximação) quando o rolo chega perto do fim —
     *  quem empilha anexa meses. */
    readonly onNearEnd: () => void;
  },
): void {
  // `onNearEnd` muda de identidade a cada render de quem empilha (é um closure
  // sobre o estado dos meses); lido como evento, o efeito não remonta a cada
  // render — e o guarda de "já pedi nesta altura" sobrevive ao render, senão um
  // heat chegando com o rolo parado no limiar pediria a mesma altura de novo.
  const askForMore = useEffectEvent(() => {
    onNearEnd();
  });
  const askedAt = useRef(-1);
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!enabled || !scroller) return undefined;
    const measure = () => {
      if (scroller.clientHeight === 0) return;
      const remaining = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
      if (remaining < scroller.clientHeight * NEAR_END_SCREENS) {
        // Um pedido por tamanho de coluna: o próximo só depois de ela crescer.
        if (askedAt.current !== scroller.scrollHeight) {
          askedAt.current = scroller.scrollHeight;
          askForMore();
        }
      }
    };
    measure();
    scroller.addEventListener('scroll', measure, { passive: true });
    return () => {
      scroller.removeEventListener('scroll', measure);
    };
  }, [enabled, scrollerRef]);
}

/**
 * The mirror of `useGrowNearEnd` for the column that grows UP (`past`, `free`): asks for more
 * months when the scroller nears the top and, as they come in ABOVE what is in view, gives the
 * scroller back the height that came in — without it the view would jump to the new months on
 * every growth. `size` is the column's length: the compensation runs when it changes.
 */
export function useGrowNearStart(
  scrollerRef: RefObject<HTMLDivElement | null>,
  {
    enabled,
    size,
    onNearStart,
  }: {
    readonly enabled: boolean;
    readonly size: number;
    readonly onNearStart: () => void;
  },
): void {
  const askForMore = useEffectEvent(() => {
    onNearStart();
  });
  const sizeNow = useEffectEvent(() => size);
  const askedAt = useRef(-1);
  // The column's height (and length) before the ask; once the length changes, the
  // height difference is what came in on top.
  const before = useRef<{ height: number; size: number } | null>(null);
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const asked = before.current;
    if (!scroller || asked === null || asked.size === size) return;
    scroller.scrollTop += scroller.scrollHeight - asked.height;
    before.current = null;
  }, [scrollerRef, size]);
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!enabled || !scroller) return undefined;
    const measure = () => {
      if (scroller.clientHeight === 0) return;
      if (scroller.scrollTop < scroller.clientHeight * NEAR_END_SCREENS) {
        if (askedAt.current !== scroller.scrollHeight) {
          askedAt.current = scroller.scrollHeight;
          before.current = { height: scroller.scrollHeight, size: sizeNow() };
          askForMore();
        }
      }
    };
    scroller.addEventListener('scroll', measure, { passive: true });
    return () => {
      scroller.removeEventListener('scroll', measure);
    };
  }, [enabled, scrollerRef]);
}

/**
 * Opens the column scrolled to the month at `index` (its `data-month-index` caption), once,
 * before paint: the `past` and `free` sheets are born on the value's month, not on the column's
 * top. `undefined` does not scroll (the downward column is born on top).
 */
export function useOpenAtMonth(
  scrollerRef: RefObject<HTMLDivElement | null>,
  index: number | undefined,
): void {
  const opened = useRef(false);
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (opened.current || index === undefined || !scroller) return;
    opened.current = true;
    const caption = scroller.querySelector(`[data-month-index="${String(index)}"]`);
    // The caption is the month's first row, which is the snap stop.
    const month = caption?.parentElement;
    if (!month) return;
    scroller.scrollTop += month.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
  }, [scrollerRef, index]);
}
